import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { Button as VButton, FormItem } from 'vnetwork-ui'
import {
    Calendar,
    TrendingUp,
    AlertTriangle,
    Clock,
    Building2,
    Zap,
    Timer,
    Bell,
    Plus,
    Trash2,
    Users,
    Mail,
    MessageSquare,
    Globe,
    FileText,
    ArrowLeft,
    Shield,
    Save,
} from 'lucide-react'

import SubHeaders from '@/components/Headers/SubHeaders'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import Paper from '@/components/Paper'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getServiceSLA,
    createServiceSLA,
    updateServiceSLA,
} from '@/modules/Service/services/sla'
import { getService } from '@/modules/Service/services'
import { ServiceSLAPayload } from '@/modules/Service/types/sla'
import { generatePathDatacentral, paths } from '@/utils/path'
import {
    TIMEZONE_OPTIONS,
    WORK_DAYS_OPTIONS,
    PRIORITY_OPTIONS,
} from '@/modules/Service/constants'

// Notification channel options with icons (extends base constants)
const NOTIFICATION_CHANNELS = [
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'sms', label: 'SMS', icon: MessageSquare },
    { value: 'slack', label: 'Slack', icon: MessageSquare },
    { value: 'teams', label: 'Teams', icon: Users },
]

// Extended form data
interface FormData extends ServiceSLAPayload {
    default_response_time?: number
    default_resolution_time?: number
}

const SLAConfigPage = () => {
    const { t } = useTranslation('services')
    const { id } = useParams()
    const navigate = useNavigate()
    const serviceId = Number(id)

    // Fetch service info
    const { data: service, isLoading: isLoadingService } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES, serviceId],
        queryFn: () => getService(serviceId),
        enabled: !!serviceId,
    })

    // Fetch current SLA
    const { data: slaData, isLoading: isLoadingSLA } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICE_SLA, serviceId],
        queryFn: () => getServiceSLA(serviceId),
        enabled: !!serviceId,
    })

    const sla = slaData?.result
    const isEditing = !!sla

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { isDirty },
    } = useForm<FormData>({
        defaultValues: {
            sla_source: 'manual',
            effective_from: new Date().toISOString().split('T')[0],
            uptime_guarantee: 99.9,
            downtime_allowance_minutes: 43,
            default_response_time: 120,
            default_resolution_time: 480,
            business_hours_only: true,
            business_hours_config: {
                timezone: 'Asia/Ho_Chi_Minh',
                work_days: [1, 2, 3, 4, 5],
                work_hours: { start: '08:00', end: '17:30' },
                include_weekends: false,
            },
            custom_terms: [],
            is_active: true,
            sla_rules: [],
            escalation_rules: [],
        },
    })

    // Field array for escalation rules
    const {
        fields: escalationFields,
        append: appendEscalation,
        remove: removeEscalation,
    } = useFieldArray({
        control,
        name: 'escalation_rules',
    })

    const {
        fields: customTermFields,
        append: appendCustomTerm,
        remove: removeCustomTerm,
    } = useFieldArray({
        control,
        name: 'custom_terms',
    })

    const watchUptime = watch('uptime_guarantee')
    const watchDowntime = watch('downtime_allowance_minutes')
    const watchResponse = watch('default_response_time')
    const watchResolution = watch('default_resolution_time')
    const watchIncludeWeekends = watch('business_hours_config.include_weekends')

    // Add new escalation rule
    const handleAddEscalation = () => {
        appendEscalation({
            condition: {
                ticket_type_group: 'default',
                priority: ['critical', 'high'],
                no_response_after_minutes: 60,
            },
            escalate_to: {
                employee_ids: [],
                roles: ['manager'],
            },
            notification_channels: ['email'],
            escalation_level: escalationFields.length + 1,
        })
    }

    // Reset form when SLA changes
    useEffect(() => {
        if (sla) {
            const slaRules = Array.isArray(sla.sla_rules) ? sla.sla_rules : []
            const defaultRule = slaRules.find(
                (r) => r.ticket_type_group === 'default'
            )
            const responseTime =
                defaultRule?.response_time_minutes ||
                sla.sla_template?.default_response_time ||
                120
            const resolutionTime =
                defaultRule?.resolution_time_minutes ||
                sla.sla_template?.default_resolution_time ||
                480

            reset({
                sla_source: sla.sla_source,
                effective_from: sla.effective_from,
                effective_to: sla.effective_to,
                uptime_guarantee: sla.uptime_guarantee,
                downtime_allowance_minutes: sla.downtime_allowance_minutes,
                default_response_time: responseTime,
                default_resolution_time: resolutionTime,
                business_hours_only: sla.business_hours_only,
                business_hours_config: sla.business_hours_config || {
                    timezone: 'Asia/Ho_Chi_Minh',
                    work_days: [1, 2, 3, 4, 5],
                    work_hours: { start: '08:00', end: '17:30' },
                    include_weekends: false,
                },
                custom_terms: sla.custom_terms || [],
                is_active: sla.is_active,
                sla_rules: sla.sla_rules || [],
                escalation_rules: sla.escalation_rules || [],
            })
        }
    }, [sla, reset])

    // Create mutation
    const { mutate: create, isPending: isCreating } = useMutationWithHandler(
        {
            mutationFn: (payload: ServiceSLAPayload) =>
                createServiceSLA(serviceId, payload),
        },
        {
            successMessage: t('sla.sla_created', 'Đã tạo cấu hình SLA'),
            reactQueryKeys: QUERY_KEYS.SERVICE_SLA,
            onSuccess: () => navigate(-1),
        }
    )

    // Update mutation
    const { mutate: update, isPending: isUpdating } = useMutationWithHandler(
        {
            mutationFn: (payload: ServiceSLAPayload) =>
                updateServiceSLA(serviceId, payload),
        },
        {
            successMessage: t('sla.sla_updated', 'Đã cập nhật cấu hình SLA'),
            reactQueryKeys: QUERY_KEYS.SERVICE_SLA,
            onSuccess: () => navigate(-1),
        }
    )

    const isPending = isCreating || isUpdating

    const onSubmit = (data: FormData) => {
        const { default_response_time, default_resolution_time, ...rest } = data
        const existingRules = Array.isArray(rest.sla_rules)
            ? rest.sla_rules
            : []
        const otherRules = existingRules.filter(
            (r) => r.ticket_type_group !== 'default'
        )

        // Format dates to RFC3339 (ISO 8601) for backend
        const formatDateForBackend = (date: string | undefined) => {
            if (!date) return undefined
            return dayjs(date).startOf('day').toISOString()
        }

        const payload: ServiceSLAPayload = {
            ...rest,
            effective_from: formatDateForBackend(rest.effective_from),
            effective_to: formatDateForBackend(rest.effective_to),
            sla_rules: [
                {
                    ticket_type_group: 'default',
                    response_time_minutes: default_response_time || 120,
                    resolution_time_minutes: default_resolution_time || 480,
                    business_hours_only: rest.business_hours_only,
                },
                ...otherRules,
            ],
        }

        const mutation = isEditing ? update : create
        mutation(payload)
    }

    if (isLoadingService || isLoadingSLA) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-96 w-full" />
            </div>
        )
    }

    return (
        <div className="space-y-2">
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.services,
                        title: t('services', 'Dịch vụ'),
                    },
                    {
                        path: generatePathDatacentral(paths.serviceDetail, {
                            id: serviceId,
                        }),
                        title:
                            [
                                service?.product_group_name,
                                service?.service_name?.toUpperCase(),
                            ].join(' - ') || `Service #${serviceId}`,
                    },
                    {
                        path: '#',
                        title: isEditing
                            ? t('sla.edit_sla')
                            : t('sla.create_sla'),
                    },
                ]}
            />

            <form onSubmit={handleSubmit(onSubmit)}>
                {/* Header Actions */}
                <Paper className="p-4 mb-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-md">
                                <Shield className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold">
                                    {isEditing
                                        ? t(
                                              'sla.edit_service_sla',
                                              'Sửa cấu hình SLA'
                                          )
                                        : t(
                                              'sla.create_service_sla',
                                              'Tạo cấu hình SLA'
                                          )}
                                </h1>
                                <p className="text-sm text-gray-500">
                                    {service?.product_group_name} -{' '}
                                    {service?.service_name?.toUpperCase()}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate(-1)}
                            >
                                <ArrowLeft className="w-4 h-4 mr-1" />
                                Quay lại
                            </Button>
                            <VButton
                                type="submit"
                                loading={isPending}
                                disabled={!isDirty && isEditing}
                            >
                                <Save className="w-4 h-4 mr-1" />
                                {isEditing ? 'Lưu thay đổi' : 'Tạo SLA'}
                            </VButton>
                        </div>
                    </div>
                </Paper>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    {/* Left Column */}
                    <div className="space-y-2">
                        {/* Effective Dates */}
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-blue-500" />
                                    <CardTitle className="text-sm">
                                        Thời gian hiệu lực
                                    </CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 gap-2">
                                    <FormItem
                                        control={control}
                                        name="effective_from"
                                        type="datepicker"
                                        label="Bắt đầu"
                                        size="small"
                                    />
                                    <FormItem
                                        control={control}
                                        name="effective_to"
                                        type="datepicker"
                                        label="Kết thúc"
                                        size="small"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* SLA Metrics */}
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center gap-2">
                                    <TrendingUp className="w-4 h-4 text-green-500" />
                                    <CardTitle className="text-sm">
                                        Chỉ số SLA
                                    </CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                        <Label className="text-xs flex items-center gap-1">
                                            <TrendingUp className="w-3 h-3 text-green-500" />
                                            Uptime (%)
                                        </Label>
                                        <Controller
                                            name="uptime_guarantee"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    type="number"
                                                    step="0.01"
                                                    className="h-9"
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            parseFloat(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                        {watchUptime && (
                                            <p className="text-xs text-gray-500">
                                                ≈{' '}
                                                {(
                                                    (100 - (watchUptime || 0)) *
                                                    7.2
                                                ).toFixed(1)}{' '}
                                                giờ downtime/tháng
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs flex items-center gap-1">
                                            <AlertTriangle className="w-3 h-3 text-orange-500" />
                                            Downtime (phút)
                                        </Label>
                                        <Controller
                                            name="downtime_allowance_minutes"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    type="number"
                                                    className="h-9"
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            parseInt(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                        {watchDowntime && (
                                            <p className="text-xs text-gray-500">
                                                ≈{' '}
                                                {(
                                                    (watchDowntime || 0) / 60
                                                ).toFixed(1)}{' '}
                                                giờ/tháng
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                                    <div className="space-y-1">
                                        <Label className="text-xs flex items-center gap-1">
                                            <Clock className="w-3 h-3 text-blue-500" />
                                            Phản hồi (phút)
                                        </Label>
                                        <Controller
                                            name="default_response_time"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    type="number"
                                                    className="h-9"
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            parseInt(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                        {watchResponse &&
                                            watchResponse >= 60 && (
                                                <p className="text-xs text-gray-500">
                                                    ={' '}
                                                    {(
                                                        watchResponse / 60
                                                    ).toFixed(1)}{' '}
                                                    giờ
                                                </p>
                                            )}
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs flex items-center gap-1">
                                            <Timer className="w-3 h-3 text-purple-500" />
                                            Xử lý (phút)
                                        </Label>
                                        <Controller
                                            name="default_resolution_time"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    type="number"
                                                    className="h-9"
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            parseInt(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                        {watchResolution &&
                                            watchResolution >= 60 && (
                                                <p className="text-xs text-gray-500">
                                                    ={' '}
                                                    {(
                                                        watchResolution / 60
                                                    ).toFixed(1)}{' '}
                                                    giờ
                                                </p>
                                            )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Settings */}
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center gap-2">
                                    <Zap className="w-4 h-4 text-yellow-500" />
                                    <CardTitle className="text-sm">
                                        Cài đặt
                                    </CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <Building2 className="w-4 h-4 text-blue-500" />
                                        <div>
                                            <Label className="text-sm">
                                                Chỉ tính giờ làm việc
                                            </Label>
                                            <p className="text-xs text-gray-500">
                                                SLA chỉ tính trong giờ hành
                                                chính
                                            </p>
                                        </div>
                                    </div>
                                    <Controller
                                        name="business_hours_only"
                                        control={control}
                                        render={({ field }) => (
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <Zap className="w-4 h-4 text-green-500" />
                                        <div>
                                            <Label className="text-sm">
                                                Kích hoạt SLA
                                            </Label>
                                            <p className="text-xs text-gray-500">
                                                SLA đang hoạt động
                                            </p>
                                        </div>
                                    </div>
                                    <Controller
                                        name="is_active"
                                        control={control}
                                        render={({ field }) => (
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        )}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-2">
                        {/* Business Hours Config */}
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-indigo-500" />
                                    <CardTitle className="text-sm">
                                        Cấu hình giờ làm việc
                                    </CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <div>
                                        <Label className="text-sm">
                                            Tính 24/7
                                        </Label>
                                        <p className="text-xs text-gray-500">
                                            SLA được tính liên tục
                                        </p>
                                    </div>
                                    <Controller
                                        name="business_hours_config.include_weekends"
                                        control={control}
                                        render={({ field }) => (
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        )}
                                    />
                                </div>
                                {!watchIncludeWeekends && (
                                    <>
                                        <div className="space-y-1">
                                            <Label className="text-xs">
                                                Múi giờ
                                            </Label>
                                            <Controller
                                                name="business_hours_config.timezone"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        value={field.value}
                                                        onValueChange={
                                                            field.onChange
                                                        }
                                                    >
                                                        <SelectTrigger className="h-9">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {TIMEZONE_OPTIONS.map(
                                                                (tz) => (
                                                                    <SelectItem
                                                                        key={
                                                                            tz.value
                                                                        }
                                                                        value={
                                                                            tz.value
                                                                        }
                                                                    >
                                                                        {
                                                                            tz.label
                                                                        }
                                                                    </SelectItem>
                                                                )
                                                            )}
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="space-y-1">
                                                <Label className="text-xs">
                                                    Giờ bắt đầu
                                                </Label>
                                                <Controller
                                                    name="business_hours_config.work_hours.start"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            {...field}
                                                            type="time"
                                                            className="h-9"
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs">
                                                    Giờ kết thúc
                                                </Label>
                                                <Controller
                                                    name="business_hours_config.work_hours.end"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            {...field}
                                                            type="time"
                                                            className="h-9"
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">
                                                Ngày làm việc
                                            </Label>
                                            <Controller
                                                name="business_hours_config.work_days"
                                                control={control}
                                                render={({ field }) => (
                                                    <div className="flex flex-wrap gap-1">
                                                        {WORK_DAYS_OPTIONS.map(
                                                            (day) => {
                                                                const isSelected =
                                                                    field.value?.includes(
                                                                        day.value
                                                                    )
                                                                return (
                                                                    <Button
                                                                        key={
                                                                            day.value
                                                                        }
                                                                        type="button"
                                                                        variant={
                                                                            isSelected
                                                                                ? 'default'
                                                                                : 'outline'
                                                                        }
                                                                        size="sm"
                                                                        className="h-7 min-w-[36px] px-2 text-xs"
                                                                        onClick={() => {
                                                                            const current =
                                                                                field.value ||
                                                                                []
                                                                            const newVal =
                                                                                isSelected
                                                                                    ? current.filter(
                                                                                          (
                                                                                              v
                                                                                          ) =>
                                                                                              v !==
                                                                                              day.value
                                                                                      )
                                                                                    : [
                                                                                          ...current,
                                                                                          day.value,
                                                                                      ].sort()
                                                                            field.onChange(
                                                                                newVal
                                                                            )
                                                                        }}
                                                                    >
                                                                        {
                                                                            day.label
                                                                        }
                                                                    </Button>
                                                                )
                                                            }
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>

                        {/* Custom Terms */}
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-amber-500" />
                                    <CardTitle className="text-sm">
                                        Điều khoản tùy chỉnh
                                    </CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {customTermFields.length === 0 ? (
                                        <div className="text-center py-4 bg-gray-50 rounded-lg">
                                            <p className="text-sm text-gray-500 mb-2">
                                                Chưa có điều khoản
                                            </p>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    appendCustomTerm({
                                                        key: '',
                                                        value: '',
                                                    })
                                                }
                                            >
                                                <Plus className="w-4 h-4 mr-1" />
                                                Thêm
                                            </Button>
                                        </div>
                                    ) : (
                                        <>
                                            {customTermFields.map(
                                                (field, index) => (
                                                    <div
                                                        key={field.id}
                                                        className="space-y-1"
                                                    >
                                                        <div className="flex gap-2 items-center">
                                                            <Controller
                                                                name={`custom_terms.${index}.key`}
                                                                control={
                                                                    control
                                                                }
                                                                render={({
                                                                    field,
                                                                }) => (
                                                                    <Input
                                                                        {...field}
                                                                        className="h-8 flex-1 text-sm"
                                                                        placeholder="Tên điều khoản"
                                                                    />
                                                                )}
                                                            />
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() =>
                                                                    removeCustomTerm(
                                                                        index
                                                                    )
                                                                }
                                                                className="h-8 w-8 p-0 text-red-500"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                        <Controller
                                                            name={`custom_terms.${index}.value`}
                                                            control={control}
                                                            render={({
                                                                field,
                                                            }) => (
                                                                <TextEditorCustomize
                                                                    value={{
                                                                        text:
                                                                            field.value ||
                                                                            '',
                                                                        attachments:
                                                                            [],
                                                                    }}
                                                                    onChange={(
                                                                        val
                                                                    ) =>
                                                                        field.onChange(
                                                                            val.text
                                                                        )
                                                                    }
                                                                    disabledAttachments
                                                                    placeholder="Nội dung điều khoản..."
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                )
                                            )}
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    appendCustomTerm({
                                                        key: '',
                                                        value: '',
                                                    })
                                                }
                                                className="w-full"
                                            >
                                                <Plus className="w-4 h-4 mr-1" />
                                                Thêm điều khoản
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* Escalation Rules - Full Width */}
                <Card className="mt-2">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Bell className="w-4 h-4 text-red-500" />
                                <CardTitle className="text-sm">
                                    {t('sla.escalation_rules')}
                                </CardTitle>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleAddEscalation}
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                Thêm cảnh báo
                            </Button>
                        </div>
                        <CardDescription className="text-xs">
                            Tự động thông báo khi ticket sắp quá hạn SLA
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {escalationFields.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <Bell className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                                <p className="text-sm text-gray-500 mb-2">
                                    Chưa có cảnh báo SLA
                                </p>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleAddEscalation}
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Thêm cảnh báo
                                </Button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {escalationFields.map((field, index) => (
                                    <div
                                        key={field.id}
                                        className="p-3 border rounded-lg bg-gray-50/50 space-y-2"
                                    >
                                        <div className="flex items-center justify-between">
                                            <Badge
                                                variant="outline"
                                                className="text-xs"
                                            >
                                                Cảnh báo #{index + 1}
                                            </Badge>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                    removeEscalation(index)
                                                }
                                                className="h-6 w-6 p-0 text-red-500"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>

                                        <div className="space-y-1">
                                            <Label className="text-xs">
                                                Cảnh báo trước (phút)
                                            </Label>
                                            <Controller
                                                name={`escalation_rules.${index}.condition.no_response_after_minutes`}
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        {...field}
                                                        type="number"
                                                        className="h-8 text-sm"
                                                        onChange={(e) =>
                                                            field.onChange(
                                                                parseInt(
                                                                    e.target
                                                                        .value
                                                                ) || 0
                                                            )
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <Label className="text-xs">
                                                Áp dụng cho ticket
                                            </Label>
                                            <Controller
                                                name={`escalation_rules.${index}.condition.priority`}
                                                control={control}
                                                render={({ field }) => (
                                                    <div className="flex flex-wrap gap-1">
                                                        {PRIORITY_OPTIONS.map(
                                                            (p) => (
                                                                <Badge
                                                                    key={
                                                                        p.value
                                                                    }
                                                                    variant={
                                                                        field.value?.includes(
                                                                            p.value
                                                                        )
                                                                            ? 'default'
                                                                            : 'outline'
                                                                    }
                                                                    className="cursor-pointer text-xs"
                                                                    onClick={() => {
                                                                        const current =
                                                                            field.value ||
                                                                            []
                                                                        const newVal =
                                                                            current.includes(
                                                                                p.value
                                                                            )
                                                                                ? current.filter(
                                                                                      (
                                                                                          v
                                                                                      ) =>
                                                                                          v !==
                                                                                          p.value
                                                                                  )
                                                                                : [
                                                                                      ...current,
                                                                                      p.value,
                                                                                  ]
                                                                        field.onChange(
                                                                            newVal
                                                                        )
                                                                    }}
                                                                >
                                                                    {p.label}
                                                                </Badge>
                                                            )
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <Label className="text-xs">
                                                Kênh thông báo
                                            </Label>
                                            <Controller
                                                name={`escalation_rules.${index}.notification_channels`}
                                                control={control}
                                                render={({ field }) => (
                                                    <div className="flex flex-wrap gap-1">
                                                        {NOTIFICATION_CHANNELS.map(
                                                            (ch) => {
                                                                const Icon =
                                                                    ch.icon
                                                                const isActive =
                                                                    field.value?.includes(
                                                                        ch.value
                                                                    )
                                                                return (
                                                                    <Button
                                                                        key={
                                                                            ch.value
                                                                        }
                                                                        type="button"
                                                                        variant={
                                                                            isActive
                                                                                ? 'default'
                                                                                : 'outline'
                                                                        }
                                                                        size="sm"
                                                                        className="h-7 text-xs"
                                                                        onClick={() => {
                                                                            const current =
                                                                                field.value ||
                                                                                []
                                                                            const newVal =
                                                                                isActive
                                                                                    ? current.filter(
                                                                                          (
                                                                                              v
                                                                                          ) =>
                                                                                              v !==
                                                                                              ch.value
                                                                                      )
                                                                                    : [
                                                                                          ...current,
                                                                                          ch.value,
                                                                                      ]
                                                                            field.onChange(
                                                                                newVal
                                                                            )
                                                                        }}
                                                                    >
                                                                        <Icon className="w-3 h-3 mr-1" />
                                                                        {
                                                                            ch.label
                                                                        }
                                                                    </Button>
                                                                )
                                                            }
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <Label className="text-xs">
                                                Gửi đến vai trò
                                            </Label>
                                            <Controller
                                                name={`escalation_rules.${index}.escalate_to.roles`}
                                                control={control}
                                                render={({ field }) => (
                                                    <div className="flex flex-wrap gap-1">
                                                        {[
                                                            'manager',
                                                            'team_lead',
                                                            'admin',
                                                        ].map((role) => (
                                                            <Badge
                                                                key={role}
                                                                variant={
                                                                    field.value?.includes(
                                                                        role
                                                                    )
                                                                        ? 'default'
                                                                        : 'outline'
                                                                }
                                                                className="cursor-pointer text-xs capitalize"
                                                                onClick={() => {
                                                                    const current =
                                                                        field.value ||
                                                                        []
                                                                    const newVal =
                                                                        current.includes(
                                                                            role
                                                                        )
                                                                            ? current.filter(
                                                                                  (
                                                                                      v
                                                                                  ) =>
                                                                                      v !==
                                                                                      role
                                                                              )
                                                                            : [
                                                                                  ...current,
                                                                                  role,
                                                                              ]
                                                                    field.onChange(
                                                                        newVal
                                                                    )
                                                                }}
                                                            >
                                                                <Users className="w-3 h-3 mr-1" />
                                                                {role.replace(
                                                                    '_',
                                                                    ' '
                                                                )}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </form>
        </div>
    )
}

export default SLAConfigPage
